/**
 * The password policy, once, for every form that sets a password.
 *
 * These rules MUST mirror `backend/app/core/password_policy.py`
 * (`settings.password_min_length` = 12, plus three of four character classes,
 * plus a common-password list). Before this existed, signup advertised "min 8
 * characters" while the server enforced 12 — a 9-character password round
 * tripped to the API and came back as a raw pydantic string.
 *
 * The common-password list is deliberately NOT duplicated client-side: it is
 * long, it changes, and a client copy would be a second source of truth. That
 * check stays server-side and surfaces as a normal field error on submit.
 */
import type { Rule } from "antd/es/form";

export const PASSWORD_MIN_LENGTH = 12;

/** Number of the four character classes a password must draw from. */
export const PASSWORD_MIN_CLASSES = 3;

export const PASSWORD_PLACEHOLDER =
  "Password (12+ characters, mix of letters, numbers, symbols)";

export function countCharacterClasses(value: string): number {
  return [
    /[a-z]/.test(value),
    /[A-Z]/.test(value),
    /[0-9]/.test(value),
    /[^A-Za-z0-9]/.test(value),
  ].filter(Boolean).length;
}

/**
 * antd Form rules enforcing the policy client-side, so a password that cannot
 * possibly succeed never reaches the API.
 */
export function passwordRules(required = true): Rule[] {
  const rules: Rule[] = [];
  if (required) {
    rules.push({ required: true, message: "Please enter a password" });
  }
  rules.push({
    validator(_rule, value: string | undefined) {
      if (!value) return Promise.resolve();
      if (value.length < PASSWORD_MIN_LENGTH) {
        return Promise.reject(
          new Error(
            `Password must be at least ${PASSWORD_MIN_LENGTH} characters`,
          ),
        );
      }
      if (countCharacterClasses(value) < PASSWORD_MIN_CLASSES) {
        return Promise.reject(
          new Error(
            "Password must include at least three of: lowercase, uppercase, number, symbol",
          ),
        );
      }
      return Promise.resolve();
    },
  });
  return rules;
}
